import { useEffect, useState, useRef } from "react";
import "./Game.css";
import TacticSelect from "./tacticsSelect.js";
import QuestionBox from "./questionBox.js";
import questions from "../../data/questions";
import AnswerBox from "./answerBox.js";

function Game() {
  const [isMuted, setIsMuted] = useState(false);
  const audio = useRef(new Audio("/assets/audio/bg-noise.mp3"));
  const [gameState, setGameState] = useState("gameStartPage");
  const wrapperRef = useRef();
  const [scaleX, setScaleX] = useState(1);
  const [scaleY, setScaleY] = useState(1);
  const [questionSet, setQuestionSet] = useState([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answerResult, setAnswerResult] = useState(null); // "correct" | "incorrect"
  const [questionState, setQuestionState] = useState(null);
  const [score, setScore] = useState(0);
  const [isFading, setIsFading] = useState(false);
  const [justEnteredGame, setJustEnteredGame] = useState(false);
  const handleExitGame = () => {if (window.confirm("Terminate analyst session? Your progress will not be saved."))
  {
    window.close();
  }
};

   useEffect(() => {
      const timer = setTimeout(() => {
      setGameState("gameWelcomeBackground");
   }, 4000);

      const sound = audio.current;
      sound.loop = true;
      sound.volume = 0.6;
   
      sound.play().catch(() => {
      console.warn("Autoplay blocked. Will play on user interaction.");
      });
   
      return () => {
      clearTimeout(timer);
      sound.pause();
      };
   }, []);

   const toggleMute = () => {
      const sound = audio.current;
      const newMuteState = !sound.muted;
    
      sound.muted = newMuteState;
      setIsMuted(newMuteState);
    
      if (!newMuteState) {
        sound.play().catch(() => {});
      }
    };

   useEffect(() => {
      const handleResize = () => {
         setScaleX(window.innerWidth / 1280);
         setScaleY(window.innerHeight / 720);
      };

      handleResize();
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
   }, []);

  const welcomeTitle = "moderation protocol activated_"; 
  const welcomeText = 
  "Welcome to your first day as a Junior Content Analyst.\n\n" +
  "You are now authorised to begin moderation of flagged social media content.\n\n" +
  "Some posts are clean. Others contain multiple tactics. Trust your judgment.\n" +
  "Some posts may include attached images. These are not decorative.\n" +
  "Pay close attention to the imagery. Users often say one thing and post another.\n\n" +
  "Now, before you dive headfirst into flagging like a caffeinated intern with something to prove...\n"+
  "I strongly recommend you review the Learn section first.\n\n" +
  "Yes, that thing you definitely weren’t going to skip.\n"+
  "It covers common manipulation tactics. Reading it may help you avoid embarrassing yourself.\n\n"+
  "Oh and one more thing... Do not be paranoid.\n\n" +
  "Excessive suspicion will be logged." +
  " We’ve already had to recalibrate two analysts this quarter.\n" +
  "Analysts who overflag content tend to burn out. That becomes a paperwork problem... For us.\n\n" +
  "Your psychological stability is monitored via the Analyst Sanity Index (ASI) on the right. Stay within range.\n" +
  "Accuracy affects your performance rating, compensation, and psychological stability.\n\n" +
  "Do not overthink. Do not underperform.\n\n" +
  "You may proceed.";

  const currentQuestion = questionSet[questionIndex];

  const totalPossible = questionSet.length;

  const startGame = () => { setIsFading(true);
    setTimeout(() => {
      setScore(0);
      const shuffled = [...questions].sort(() => 0.5 - Math.random()).slice(0, 5);
      setQuestionSet(shuffled);
      setQuestionIndex(0);
      setGameState("gameMainBackground");

      setIsFading(false);
      setJustEnteredGame(true);

      setTimeout(() => setJustEnteredGame(false), 1000);
    }, 1000);
  };

const checkAnswer = (userAnswers) => {
  if (questionState === "answered") return;

  const selected = new Set(userAnswers);
  const required = new Set(currentQuestion.answers);
  const optional = new Set(currentQuestion.alsoAcceptable || []);
  const correct = new Set([...required, ...optional]); // only declare once

  const missed = [...required].filter(x => !selected.has(x)); // only required ones
  const extra = [...selected].filter(x => !correct.has(x));   // only truly wrong ones
  const correctSelected = [...selected].filter(x => correct.has(x));

  const majorityThreshold = Math.ceil(required.size / 2); // based on required
  const gotMajority = correctSelected.filter(x => required.has(x)).length >= majorityThreshold;

  const selectedEverything = selected.size >= 10;
  const tooManyWrong = extra.length > 2;

  const isCorrect = gotMajority && !selectedEverything && !tooManyWrong;

  if (isCorrect) {
    setScore(prev => prev + 1);
  }

  setAnswerResult({
  missed,
  extra,
  isCorrect,
  selectedEverything,
  tooManyWrong,
  selected: userAnswers
});

  setQuestionState("answered");
};

  const skipQuestion = () => {
    if(questionState === "answered") {
       return;
    }
      
    goToNextQuestion();
  
  };

   const goToNextQuestion = () => {
      setQuestionState(null);
      setAnswerResult(null);

      if (questionIndex + 1 < questionSet.length) {
         setQuestionIndex(questionIndex + 1);
      } else {
         setGameState("gameResultsBackground"); 
      }
   };

   return (
      <div className="gameCanvasContainer">
        {isFading && <div className="fadeOverlay" />}
        {justEnteredGame && <div className="fadeInOverlay" />}
        <div
          ref={wrapperRef}
          className="gameWrapper"
          style={{
            transform: `scale(${scaleX}, ${scaleY})`,
            width: "1280px",
            height: "720px",
          }}
        >

          <button onClick={toggleMute} className={`soundToggle ${isMuted ? "soundOff" : "soundOn"}`}/>

          <button className="exitButton" onClick={handleExitGame} />
    
          {gameState === "gameStartPage" ? (
            <div
              style={{
                backgroundImage: "url('/assets/images/game-art/start_page.png')",
                backgroundSize: "cover",
                backgroundPosition: "center",
                width: "100%",
                height: "100%",
                imageRendering: "pixelated",
                animation: "fadeOutStart 1s ease-in-out 3s forwards",
              }}
            />
          ) : (
            <div className={gameState}>
              {gameState === "gameWelcomeBackground" && (
                <div className="welcomeContainer">
                  <h1 className="welcomeTitle">{welcomeTitle}</h1>
                  <p className="welcomeText">
                    {welcomeText.split("\n").map((line, index) => (
                      <span key={index}>
                        {line}
                        <br />
                      </span>
                    ))}
                  </p>
                  <button className="dismissWelcome" onClick={startGame}>
                    COMMENCE MONITORING
                  </button>
                </div>
              )}
    
              {gameState === "gameMainBackground" && (
                <>
                  {questionState === "answered" && (
                    <>
                      <div className="answerBoxOverlay" />
                      <AnswerBox
                        answerResult={answerResult}
                        goToNextQuestion={goToNextQuestion}
                        question={currentQuestion}
                        alsoAcceptable={currentQuestion.alsoAcceptable}
                      />
                    </>
                  )}
    
                  <div
                    className={`gameContentWrapper ${
                      questionState === "answered" ? "blurAndBlock" : ""
                    }`}
                  >
                    <div className="tacticSelectContainer">
                      <TacticSelect
                        question={currentQuestion}
                        correctAnswers={currentQuestion.answers}
                        onSubmit={checkAnswer}
                        onSkip={skipQuestion}
                      />
                      <QuestionBox question={currentQuestion} />
                    </div>
                  </div>
                </>
              )}
    
              {gameState === "gameResultsBackground" && (
                <div className="gameResultsContainer">
                  <h2>PERFORMANCE LOGGED</h2>
                  <p className="resultsMessage">Let’s see what you’ve learned...</p>
                  <p className="resultsScore">
                      You Scored: {score} / {totalPossible}
                  </p>
                  <br></br>
                  <button className="playAgainButton" onClick={startGame}>
                    REBOOT ANALYST SESSION
                  </button>
                </div>
              )}
         </div> 
      )}
      </div>
    </div>
  ); 
}

export default Game;
